import {
  CartesianGrid,
  LabelList,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { TooltipProps } from 'recharts';
import { quadrantLabel } from '../format.ts';
import type { Quadrant, QuadrantRow } from '../types.ts';

interface Point {
  x: number;
  y: number;
  name: string;
  quadrant: Quadrant | null;
}

function QuadrantTooltip({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0]?.payload as Point | undefined;
  if (!p) return null;
  return (
    <div className="rounded-lg border border-black/10 bg-white px-3 py-2 text-xs shadow-sm">
      <div className="font-semibold text-ink">{p.name}</div>
      <div className="text-ink-soft tabular-nums">
        Mean {p.x.toFixed(2)} · SD {p.y.toFixed(2)}
      </div>
      <div className="text-ink-soft">{quadrantLabel(p.quadrant)}</div>
    </div>
  );
}

const areaLabel = { fontSize: 11, fill: '#b0b0b0', fontWeight: 600 } as const;

// View 2 — mean (x) vs SD (y) quadrant, midpoints at 3.0 / 1.0.
export function QuadrantView({ data }: { data: QuadrantRow[] }) {
  const points: Point[] = data.flatMap((d) =>
    d.mean !== null && d.sd !== null
      ? [{ x: d.mean, y: d.sd, name: d.name, quadrant: d.quadrant }]
      : [],
  );

  if (points.length === 0) {
    return <p className="py-12 text-center text-sm text-ink-soft">No mean/SD pairs available yet.</p>;
  }

  return (
    <div className="h-[460px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 20, right: 28, bottom: 32, left: 8 }}>
          <CartesianGrid stroke="#f0f0f0" />
          <XAxis
            type="number"
            dataKey="x"
            domain={[1, 5]}
            ticks={[1, 2, 3, 4, 5]}
            tick={{ fontSize: 11, fill: '#4a4a4a' }}
            label={{ value: 'Mean (maturity) →', position: 'insideBottom', offset: -16, fontSize: 12, fill: '#4a4a4a' }}
          />
          <YAxis
            type="number"
            dataKey="y"
            domain={[0, 2]}
            ticks={[0, 0.5, 1, 1.5, 2]}
            tick={{ fontSize: 11, fill: '#4a4a4a' }}
            label={{ value: 'SD (spread) →', angle: -90, position: 'insideLeft', fontSize: 12, fill: '#4a4a4a' }}
          />
          {/* Quadrant name annotations, placed by data region. */}
          <ReferenceArea x1={1} x2={3} y1={1} y2={2} fillOpacity={0} label={{ value: 'Contested Gaps', position: 'insideTopLeft', ...areaLabel }} />
          <ReferenceArea x1={3} x2={5} y1={1} y2={2} fillOpacity={0} label={{ value: 'Perception Gaps', position: 'insideTopRight', ...areaLabel }} />
          <ReferenceArea x1={1} x2={3} y1={0} y2={1} fillOpacity={0} label={{ value: 'Foundation Priorities', position: 'insideBottomLeft', ...areaLabel }} />
          <ReferenceArea x1={3} x2={5} y1={0} y2={1} fillOpacity={0} label={{ value: 'Consistent Strengths', position: 'insideBottomRight', ...areaLabel }} />
          <ReferenceLine x={3} stroke="#9ca3af" strokeDasharray="4 4" />
          <ReferenceLine y={1} stroke="#9ca3af" strokeDasharray="4 4" />
          <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<QuadrantTooltip />} />
          <Scatter data={points} fill="var(--accent)" isAnimationActive={false}>
            <LabelList dataKey="name" position="top" offset={8} fill="#4a4a4a" fontSize={9} />
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}
